import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { api } from '@/api/backend';
import { useAuthStore, useIsLoggedIn } from '@/api/login';
import { Button, RouterButton } from '@/primitives/Button';
import { ArrowLeft, Cogwheel, Icon, Logout, Plus } from '@/primitives/Icons';
import { getSourceConfig } from '@/theme';

import { useCreateOrder } from '../queries/order/crud';
import { useHydrateWindowStore } from '../queries/order/mappers';
export const ResellerHeader = () => {
  const { t } = useTranslation();
  const config = getSourceConfig();
  const Logo = config.logo;
  const location = useLocation();
  const navigate = useNavigate();
  const isWorkspacePage = location.pathname.includes('workspace');
  const isLoggedIn = useIsLoggedIn();
  const hydrateStore = useHydrateWindowStore();

  const { mutateAsync: createWindowOrder, isPending } = useCreateOrder();

  const handleCreateNewProject = async () => {
    const order = await createWindowOrder();

    hydrateStore(order);

    navigate(`/windows/orders/${order.id}/configuration`);
  };

  return (
    <>
      <header
        style={{ boxShadow: 'none', borderBottom: '1px solid var(--primary)' }}
      >
        <a href={'/windows/workspace'} className="logo">
          <Logo />
        </a>
        <div
          className="flexAlign"
          style={{
            width: '100%',
            justifyContent: 'space-between',
            marginLeft: '3rem',
          }}
        >
          <div style={{ marginLeft: 'auto' }} className="flexAlign">
            {!isWorkspacePage && isLoggedIn && (
              <RouterButton
                to="/windows/workspace"
                variant="header"
                icon={<Icon icon={<ArrowLeft />} />}
              >
                {t('common.cta.backTo', { name: t('common.cta.orders') })}
              </RouterButton>
            )}
            {isWorkspacePage && (
              <>
                <Button
                  icon={<Icon icon={<Plus />} />}
                  className="smaller header"
                  style={{
                    background: 'var(--header-cta-background)',
                    color: 'var(--header-cta-color)',
                  }}
                  disabled={isPending}
                  onClick={() => handleCreateNewProject()}
                >
                  {t('common.newOrder')}
                </Button>
              </>
            )}
            <LogoutBtn />
            <SettingsBtn />
            <Suspense fallback={'...'}>
              <LanguageSelector />
            </Suspense>
          </div>
        </div>
      </header>
    </>
  );
};

const LanguageSelector = () => {
  const { t, i18n } = useTranslation();
  const currentLng = i18n.resolvedLanguage;

  const changeLanguage = async (lng: string) => {
    i18n.changeLanguage(lng);
    await api.updateUserLeadLocale();
  };

  return (
    <div className="flexAlign gap-xxs">
      {['en', 'de'].map((lng) => {
        const style =
          lng === currentLng
            ? {
                fontWeight: 'bold',
                textDecoration: 'underline',
              }
            : undefined;
        return (
          <Button
            key={lng}
            variant="iconHeader"
            onClick={async () => await changeLanguage(lng)}
            style={style}
          >
            {t(`common.lng.${lng}`)}
          </Button>
        );
      })}
    </div>
  );
};

const LogoutBtn = () => {
  const { logout } = useAuthStore();
  const { t } = useTranslation();
  const { email } = useAuthStore();
  const isLoggedIn = useIsLoggedIn();

  if (!isLoggedIn) return null;

  const onLogout = () => {
    logout();
  };

  return (
    <Button
      variant="header"
      icon={<Icon icon={<Logout />} />}
      onClick={onLogout}
      className="tooltipBottom"
      tooltip={email ?? undefined}
    >
      {t('common.logout')}
    </Button>
  );
};

const SettingsBtn = () => {
  const { t } = useTranslation();
  const isLoggedIn = useIsLoggedIn();

  if (!isLoggedIn) return null;

  return (
    <RouterButton
      variant="header"
      icon={<Icon icon={<Cogwheel />} />}
      to="/windows/settings"
      className="tooltipBottom"
    >
      {t('common.settings')}
    </RouterButton>
  );
};
